import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText, Users, Calendar, Printer, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { teachersAPI, classesAPI } from '../services/api';
import { attendanceAPI } from '../services/api';
import api from '../services/api';
import { LoadingPage } from '../components/common';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const GRADE_LABEL = g => (['PreKG', 'LKG', 'UKG'].includes(g) ? g : `Std ${g}`);

// ─── PDF Generation (client-side using print) ──────────────────────
const generatePDF = (content, filename) => {
  const win = window.open('', '_blank');
  win.document.write(content);
  win.document.close();
  setTimeout(() => {
    win.focus();
    win.print();
  }, 500);
};

// Build student details PDF HTML
const buildStudentDetailsPDF = (data) => {
  const { ministry, vbsTitle, tagline, vbsYear, class: cls, teacher, students, generatedAt, generatedBy } = data;

  const rows = students.map((s, i) => `
    <tr class="${i % 2 === 0 ? '' : 'alt'}">
      <td>${s.sno}</td>
      <td>${s.studentId || '—'}</td>
      <td>${s.name}</td>
      <td>${GRADE_LABEL(s.grade)}</td>
      <td>${s.gender === 'male' ? 'M' : s.gender === 'female' ? 'F' : 'O'}</td>
      <td>${s.religion}${s.christianDenomination ? ` (${s.christianDenomination})` : ''}</td>
      <td>${s.contactNumber || '—'}</td>
      <td>${s.whatsappNumber || '—'}</td>
      <td>${s.parentName || '—'}</td>
      <td>${s.village || '—'}</td>
      <td>${s.schoolName || '—'}</td>
    </tr>
  `).join('');

  return `<!DOCTYPE html><html><head>
  <meta charset="UTF-8">
  <title>Student Details — ${cls.name}</title>
  <style>
    @page { size: A4 landscape; margin: 15mm 12mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 9pt; color: #1a1a1a; }
    .header { border-bottom: 3px solid #1a2f5e; padding-bottom: 10px; margin-bottom: 12px; }
    .hdr-row { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
    .hdr-logo { width: 44px; height: 44px; object-fit: contain; flex-shrink: 0; border-radius: 6px; }
    .church-name { font-size: 14pt; font-weight: 800; color: #1a2f5e; line-height: 1.2; }
    .church-sub { font-size: 8pt; color: #666; margin-top: 1px; }
    .vbs-title { font-size: 11pt; font-weight: 700; color: #c8922a; margin-top: 2px; }
    .tagline { font-size: 8.5pt; color: #555; font-style: italic; margin-top: 2px; }
    .meta { display: flex; justify-content: space-between; margin: 8px 0 12px; padding: 8px 12px; background: #f4f6fb; border-radius: 6px; font-size: 8pt; }
    .meta-item label { font-weight: 700; color: #1a2f5e; display: block; font-size: 7pt; text-transform: uppercase; letter-spacing: 0.5px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #1a2f5e; color: white; padding: 6px 8px; text-align: left; font-size: 8pt; font-weight: 700; }
    td { padding: 5px 8px; border-bottom: 1px solid #e8edf2; font-size: 8.5pt; }
    tr.alt td { background: #f9fafb; }
    .footer { margin-top: 16px; display: flex; justify-content: space-between; font-size: 7.5pt; color: #888; border-top: 1px solid #ddd; padding-top: 8px; }
    @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
  </style>
  </head><body>
  <div class="header">
    <div class="hdr-row">
      <img class="hdr-logo" src="/poj-logo.png" alt="Presence of Jesus Ministry" onerror="this.style.display='none'" />
      <div>
        <div class="church-name">Presence of Jesus Ministry</div>
        <div class="church-sub">Tuticorin, Tamil Nadu, India</div>
      </div>
    </div>
    <div class="vbs-title">${vbsTitle} — VBS ${vbsYear}</div>
    ${tagline ? `<div class="tagline">"${tagline}"</div>` : ''}
  </div>
  <div class="meta">
    <div class="meta-item"><label>Report</label>Student Details</div>
    <div class="meta-item"><label>Class</label>${cls.name} (${cls.category})</div>
    <div class="meta-item"><label>Teacher</label>${teacher?.name || '—'}</div>
    <div class="meta-item"><label>Total Students</label>${students.length}</div>
  </div>
  <table>
    <thead><tr>
      <th style="width:28px">S.No</th>
      <th style="width:70px">Student ID</th>
      <th style="width:140px">Name</th>
      <th style="width:42px">Grade</th>
      <th style="width:28px">Sex</th>
      <th style="width:100px">Religion</th>
      <th style="width:80px">Contact</th>
      <th style="width:80px">WhatsApp</th>
      <th style="width:120px">Parent Name</th>
      <th style="width:90px">Village</th>
      <th style="width:110px">School</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">
    <span>Generated by: ${generatedBy}</span>
    <span>Presence of Jesus Ministry — VBS Management System</span>
    <span>Generated on: ${generatedAt} IST</span>
  </div>
  </body></html>`;
};

// Build attendance template PDF HTML
const buildAttendanceTemplatePDF = (data) => {
  const { ministry, vbsTitle, tagline, vbsYear, class: cls, teacher, students, vbsDates, generatedAt, generatedBy } = data;

  const hasData = vbsDates.length > 0;
  const dayHeaders = hasData
    ? vbsDates.map((d, i) => `<th class="day-col">Day ${i + 1}<br><span style="font-weight:400;font-size:6.5pt">${d.dateStr}</span></th>`).join('')
    : Array.from({ length: 10 }, (_, i) => `<th class="day-col">Day ${i + 1}</th>`).join('');

  const totalDays = hasData ? vbsDates.length : 10;

  const rows = students.map((s, i) => {
    const dayCells = hasData
      ? vbsDates.map(d => {
          const status = s.attendance?.[d.dateStr];
          return `<td class="day-cell ${status === 'present' ? 'present' : status === 'absent' ? 'absent' : ''}">${status === 'present' ? '✓' : status === 'absent' ? '✗' : ''}</td>`;
        }).join('')
      : Array.from({ length: 10 }, () => '<td class="day-cell"></td>').join('');

    const total = hasData ? (s.totalPresent || 0) : '';

    return `<tr class="${i % 2 === 0 ? '' : 'alt'}">
      <td class="sno">${s.sno}</td>
      <td class="name-cell">${s.name}</td>
      <td class="grade-cell">${GRADE_LABEL(s.grade)}</td>
      ${dayCells}
      <td class="total-cell">${total}</td>
    </tr>`;
  }).join('');

  // Total row
  const totalDayCells = hasData
    ? vbsDates.map(d => {
        const count = students.filter(s => s.attendance?.[d.dateStr] === 'present').length;
        return `<td class="day-cell total-row">${count}</td>`;
      }).join('')
    : Array.from({ length: 10 }, () => '<td class="day-cell total-row"></td>').join('');

  const grandTotal = hasData ? students.reduce((sum, s) => sum + (s.totalPresent || 0), 0) : '';

  return `<!DOCTYPE html><html><head>
  <meta charset="UTF-8">
  <title>Attendance Template — ${cls.name}</title>
  <style>
    @page { size: A4 landscape; margin: 12mm 10mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 8.5pt; color: #1a1a1a; }
    .header { border-bottom: 3px solid #1a2f5e; padding-bottom: 10px; margin-bottom: 12px; }
    .hdr-row { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
    .hdr-logo { width: 44px; height: 44px; object-fit: contain; flex-shrink: 0; border-radius: 6px; }
    .church-name { font-size: 14pt; font-weight: 800; color: #1a2f5e; line-height: 1.2; }
    .church-sub { font-size: 8pt; color: #666; margin-top: 1px; }    
    .vbs-title { font-size: 10pt; font-weight: 700; color: #c8922a; margin-top: 2px; }
    .tagline { font-size: 8pt; color: #555; font-style: italic; }
    .meta { display: flex; justify-content: space-between; margin: 6px 0 10px; padding: 7px 12px; background: #f4f6fb; border-radius: 6px; font-size: 8pt; }
    .meta-item label { font-weight: 700; color: #1a2f5e; display: block; font-size: 6.5pt; text-transform: uppercase; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #1a2f5e; color: white; padding: 5px 4px; text-align: center; font-size: 7.5pt; font-weight: 700; }
    th.name-head { text-align: left; width: 160px; padding-left: 8px; }
    th.day-col { width: 36px; }
    td { padding: 4px; border: 1px solid #dde2ea; text-align: center; font-size: 8pt; }
    td.sno { width: 24px; color: #888; font-size: 7.5pt; }
    td.name-cell { text-align: left; padding-left: 8px; font-weight: 500; width: 160px; }
    td.grade-cell { width: 36px; font-size: 7.5pt; color: #555; }
    td.day-cell { width: 36px; }
    td.total-cell { width: 38px; font-weight: 700; background: #eef2ff; color: #1a2f5e; }
    td.present { background: #dcfce7; color: #15803d; font-weight: 700; font-size: 9pt; }
    td.absent { background: #fee2e2; color: #b91c1c; font-weight: 700; }
    tr.alt td { background: #f9fafb; }
    tr.alt td.present { background: #dcfce7; }
    tr.alt td.absent { background: #fee2e2; }
    tr.alt td.total-cell { background: #eef2ff; }
    .total-row { background: #1a2f5e !important; color: white !important; font-weight: 700; }
    .footer { margin-top: 12px; display: flex; justify-content: space-between; font-size: 7pt; color: #888; border-top: 1px solid #ddd; padding-top: 6px; }
    @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
  </style>
  </head><body>
  <div class="header">
    <div class="hdr-row">
      <img class="hdr-logo" src="/poj-logo.png" alt="Presence of Jesus Ministry" onerror="this.style.display='none'" />
      <div>
        <div class="church-name">Presence of Jesus Ministry</div>
        <div class="church-sub">Tuticorin, Tamil Nadu, India</div>
      </div>
    </div>
    <div class="vbs-title">${vbsTitle} — VBS ${vbsYear}</div>
    ${tagline ? `<div class="tagline">"${tagline}"</div>` : ''}
  </div>
  <div class="meta">
    <div class="meta-item"><label>Report</label>Attendance Name List</div>
    <div class="meta-item"><label>Class</label>${cls.name} (${cls.category})</div>
    <div class="meta-item"><label>Teacher</label>${teacher?.name || '—'}</div>
    <div class="meta-item"><label>Total Students</label>${students.length}</div>
    <div class="meta-item"><label>Days Recorded</label>${hasData ? vbsDates.length : 'Template'}</div>
  </div>
  <table>
    <thead><tr>
      <th style="width:24px">S.No</th>
      <th class="name-head">Name</th>
      <th style="width:36px">Grade</th>
      ${dayHeaders}
      <th style="width:38px">Total</th>
    </tr></thead>
    <tbody>
      ${rows}
      <tr>
        <td class="total-row" colspan="3" style="text-align:right;padding-right:8px">Total Present →</td>
        ${totalDayCells}
        <td class="total-cell" style="background:#1a2f5e;color:white;font-weight:700">${grandTotal}</td>
      </tr>
    </tbody>
  </table>
  <div class="footer">
    <span>Generated by: ${generatedBy}</span>
    <span>Presence of Jesus Ministry — VBS Management System</span>
    <span>Generated on: ${generatedAt} IST</span>
  </div>
  </body></html>`;
};

// ─── Main Teacher Export Page ─────────────────────────────────────
export function TeacherExportPage() {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(null);

  const { data: exportData, isLoading, error, refetch } = useQuery({
    queryKey: ['teacher-export-data', user._id],
    queryFn: () => api.get('/teacher/export-data').then(r => r.data?.data),
    retry: false,
  });

  const handleExport = async (type) => {
    if (!exportData) return;
    setExporting(type);
    try {
      const html = type === 'students'
        ? buildStudentDetailsPDF(exportData)
        : buildAttendanceTemplatePDF(exportData);
      generatePDF(html, type === 'students' ? 'student-details' : 'attendance-template');
    } catch (err) {
      toast.error('Export failed. Please try again.');
    } finally {
      setTimeout(() => setExporting(null), 1000);
    }
  };

  if (isLoading) return <LoadingPage />;

  if (error) {
    return (
      <div className="empty-state">
        <Users size={36} style={{ color: 'var(--color-text-muted)' }} />
        <h3>No class assigned</h3>
        <p>You don't have a class assigned yet. Please contact the administrator.</p>
      </div>
    );
  }

  const { class: cls, teacher, students, vbsDates, vbsTitle, vbsYear } = exportData || {};

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Export & Reports</h1>
          <p className="page-subtitle">Download PDF reports for {cls?.name}</p>
        </div>
      </div>

      {/* Class info */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
            {[
              { label: 'Class', value: cls?.name || '—' },
              { label: 'Category', value: cls?.category || '—' },
              { label: 'Total Students', value: students?.length || 0 },
              { label: 'Attendance Days Recorded', value: vbsDates?.length || 0 },
            ].map(item => (
              <div key={item.label}>
                <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 3 }}>{item.label}</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: 'var(--color-primary)' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Export buttons */}
      <div className="grid-2" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ width: 60, height: 60, borderRadius: 16, background: 'var(--color-info-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <FileText size={28} color="var(--color-info)" />
            </div>
            <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 6 }}>Student Details</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
              Complete student list with contact details, religion, parent info, village, and school for {cls?.name}
            </div>
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => handleExport('students')} disabled={exporting === 'students'}>
              {exporting === 'students' ? <><div className="spinner spinner-sm" /> Preparing...</> : <><Download size={16} /> Export Student Details (PDF)</>}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 32 }}>
            <div style={{ width: 60, height: 60, borderRadius: 16, background: 'var(--color-success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Calendar size={28} color="var(--color-success)" />
            </div>
            <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 6 }}>Attendance Name List</div>
            <div style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
              Attendance grid: S.No, Name, Grade, Day-1 to Day-N with dates, and Total column. Includes recorded data {vbsDates?.length ? `(${vbsDates.length} days)` : '(blank template)'}
            </div>
            <button className="btn btn-success" style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => handleExport('attendance')} disabled={exporting === 'attendance'}>
              {exporting === 'attendance' ? <><div className="spinner spinner-sm" /> Preparing...</> : <><Download size={16} /> Export Attendance Template (PDF)</>}
            </button>
          </div>
        </div>
      </div>

      {/* Student list preview */}
      {students?.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Students in {cls?.name}</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{students.length} students</span>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>S.No</th><th>Student ID</th><th>Name</th><th>Grade</th>
                  <th>Contact</th><th>Parent</th><th>Village</th>
                  {vbsDates?.length > 0 && <><th>Present</th><th>Total Days</th><th>%</th></>}
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.studentId || s.sno}>
                    <td style={{ color: 'var(--color-text-muted)', fontSize: '0.78rem' }}>{s.sno}</td>
                    <td><span className="code">{s.studentId || '—'}</span></td>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td>{GRADE_LABEL(s.grade)}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>{s.contactNumber || '—'}</td>
                    <td style={{ color: 'var(--color-text-secondary)', fontSize: '0.82rem' }}>{s.parentName || '—'}</td>
                    <td style={{ color: 'var(--color-text-secondary)', fontSize: '0.82rem' }}>{s.village || '—'}</td>
                    {vbsDates?.length > 0 && (
                      <>
                        <td><span style={{ color: 'var(--color-success)', fontWeight: 700 }}>{s.totalPresent}</span></td>
                        <td>{s.totalDays}</td>
                        <td>
                          <span style={{ fontWeight: 700, color: s.percentage >= 80 ? 'var(--color-success)' : s.percentage >= 60 ? 'var(--color-warning)' : 'var(--color-danger)' }}>
                            {s.percentage}%
                          </span>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
